import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import React from 'react';

import { Technician } from '_lib/api/apiTypes.shim';
import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { Card } from '_lib/components/Card/Card';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemButton } from '_lib/components/ListItemButton/ListItemButton';
import { ListItemIcon } from '_lib/components/ListItemIcon/ListItemIcon';
import { Stack } from '_lib/components/Stack/Stack';
import { Switch } from '_lib/components/Switch/Switch';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { formatPhone, getFullName } from '_lib/utils/helpers';
import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

import { SimpleErrorDisplay } from '../SimpleErrorDisplay/SimpleErrorDisplay';

export interface TechAssignCardProps extends FieldIssueProps {
  onClearAssignedTech: () => void;
  onOpenTechAssign: () => void;
  assignedTech?: Technician;
  onSetOverrideEmployeeId?: (overrideEmployeeId: boolean) => void;
  overrideEmployeeId?: boolean;
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 5,
  borderColor: theme.palette.calendar.techAssign
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.primary.main
}));

export const TechAssignCard = (props: TechAssignCardProps): React.ReactElement => {
  const {
    onClearAssignedTech,
    onSetOverrideEmployeeId,
    onOpenTechAssign,
    assignedTech,
    issues,
    issuePath,
    matchPartial,
    overrideEmployeeId
  } = props;

  return (
    <Box>
      <Card variant='outlined'>
        <Stack
          direction='row'
          sx={{ justifyContent: 'space-between', alignItems: 'center', marginLeft: 0.75, marginTop: 1 }}>
          <Typography variant='body1Bold' text={'Tech Assignment'} />
          {assignedTech && (
            <Tooltip title='Clear Tech' placement='bottom'>
              <IconButton sx={{ paddingY: 0.5, paddingRight: 0.25 }} onClick={onClearAssignedTech}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Box sx={{ margin: 0.75 }}>
          <Box>
            {!assignedTech && (
              <>
                <Button
                  fullWidth
                  variant='contained'
                  onClick={onOpenTechAssign}
                  startIcon={<AssignmentIndIcon />}
                  text={'ASSIGN TECH'}
                />
                <SimpleErrorDisplay issues={issues} issuePath={issuePath} matchPartial={matchPartial} />
              </>
            )}
            {assignedTech && (
              <StyledListItem disablePadding>
                <ListItemButton onClick={onOpenTechAssign} disableRipple disableTouchRipple>
                  <StyledListItemIcon>
                    <AssignmentIndIcon />
                  </StyledListItemIcon>
                  <Stack spacing={0}>
                    <Typography text={getFullName(assignedTech)} />
                    <Typography>Technician</Typography>
                    {assignedTech.phoneNumber && <Typography>{formatPhone(assignedTech.phoneNumber)}</Typography>}
                    {!assignedTech.phoneNumber && <Typography>No phone number</Typography>}
                  </Stack>
                </ListItemButton>
              </StyledListItem>
            )}
          </Box>

          {onSetOverrideEmployeeId && (
            <Box sx={{ marginTop: 1, alignContent: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    color='primary'
                    checked={!!overrideEmployeeId}
                    onChange={(e) => {
                      if (!e.target.checked) onSetOverrideEmployeeId(false);
                      else onSetOverrideEmployeeId(true);
                    }}
                  />
                }
                label={<Typography text='Override Field Requirements' />}
              />
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};
